// What a Pipeline card says and which two quick actions it offers (Pipeline proposal D-03, D-14). Pure: tested in pipelineCard.test.js.

const DAY = 86400000;
const toDay = (iso) => Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10));

export function daysBetween(fromIso, toIso) {
  return Math.round((toDay(toIso) - toDay(fromIso)) / DAY);
}

// { text, tone } for the next action's due date. `today` is a "YYYY-MM-DD" string (UTC, like the server's task rule).
export function dueLabel(action, today) {
  if (!action || !action.due_date) return { text: "Sans échéance", tone: "muted" };
  const late = daysBetween(action.due_date, today);
  if (late > 0) return { text: `En retard de ${late} j`, tone: "danger" };
  if (late === 0) return { text: "Aujourd'hui", tone: "warning" };
  const [y, m, d] = action.due_date.split("-");
  return { text: `Échéance ${d}/${m}${late < -300 ? `/${y}` : ""}`, tone: "muted" };
}

export function inactiveLabel(card, now = new Date()) {
  if (!card.dormant) return null;
  if (!card.last_activity_at) return "Jamais d'activité";
  const days = Math.max(1, Math.floor((now - new Date(card.last_activity_at)) / DAY));
  return `Inactif ${days} j`;
}

// Stages where the card carries a « next action » line: every open one except a finished client; never on a lost prospect.
export const NEXT_ACTION_STAGES = ["new_lead", "contacted", "meeting_scheduled", "qualified", "demo_negotiation", "won", "onboarding_in_progress"];

// The ≤ 2 permanent actions of a card, already filtered by the person's rights (an action without the right is absent).
// ids: call | plan | note | start_onboarding | open_onboarding | open_customer | reopen
export function quickActions(card, { can, hasCustomer }) {
  const note = can.addInteraction ? { id: "note", label: "Note" } : null;
  let first = null;
  switch (card.status) {
    case "won":
      first = can.manageOnboarding ? { id: "start_onboarding", label: "Lancer l'onboarding" } : null;
      break;
    case "onboarding_in_progress":
      first = { id: "open_onboarding", label: "Onboarding" };
      break;
    case "active_client":
      first = hasCustomer ? { id: "open_customer", label: "Dossier client" } : (card.phone ? { id: "call", label: "Appeler" } : null);
      break;
    case "lost":
      first = can.changeStatus ? { id: "reopen", label: "Rouvrir" } : null;
      break;
    default:
      first = card.phone ? { id: "call", label: "Appeler" } : (can.createTask ? { id: "plan", label: "Planifier" } : null);
  }
  return [first, note].filter(Boolean);
}

const fold = (v) => String(v ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

// « Dakar, Dakar » says nothing more than « Dakar »: a commune equal to the region (accents and case ignored) is shown once.
export function locationLabel(card) {
  const parts = [card.commune, card.region].filter(Boolean);
  if (parts.length === 2 && fold(parts[0]) === fold(parts[1])) return parts[0];
  return parts.join(", ");
}

// Stages a lost prospect may be taken back to: the commercial ones only (the system-managed stages are reached by their own gestures).
export const REOPEN_STAGES = ["new_lead", "contacted", "meeting_scheduled", "qualified", "demo_negotiation"];
